/**
 * @module ics
 * Utilities for generating ICS (iCalendar) event files.
 * ICS is a standard format for exchanging calendar information,
 * used by applications like Google Calendar, Outlook, and Apple Calendar.
 */

import { NovaDate, DateLike } from './nova-date';

type IcsLang = 'EN' | 'PT';

export enum IcsMethod {
  REQUEST = 'REQUEST',
  PUBLISH = 'PUBLISH'
}

export type IcsEntity = {
  name?: string;
  email?: string;
  rsvp?: boolean;
};

export type IcsOpts = {
  uid: string;
  title: string;
  description: string;
  location: string;
  start: DateLike;
  end: DateLike;
  company: string;
  product: string;
  transparent?: boolean;
  method?: IcsMethod;
  language?: IcsLang;
  version?: string;
  organizer?: IcsEntity;
  attendees?: IcsEntity[];
};

/**
 * Generates an ICS (iCalendar) event string from the provided options.
 *
 * The ICS format is used for exchanging calendar events between applications.
 * This function builds a valid ICS event using the supplied metadata, organizer, attendees, and timing information.
 *
 * @param opts - Options describing the event details, organizer, attendees, and calendar metadata.
 * @returns The ICS event as a string, suitable for sharing or importing into calendar applications.
 */
export function icsCreate(opts: IcsOpts): string {
  const {
    title,
    description,
    location,
    start,
    end,
    uid,
    company,
    product,
    organizer,
    attendees = [],
    method = IcsMethod.REQUEST,
    language = 'EN',
    version = '2.0',
    transparent = false
  } = opts;

  const prodid = `-//${company}//${product}//${language}`;
  const toTs = (d: DateLike) =>
    new NovaDate(d).toDateTime().toUTC().toFormat("yyyyMMdd'T'HHmmss'Z'");
  const entityToStr = (e: IcsEntity) =>
    `"${e.name || 'Unknown'}";RSVP=${e.rsvp?.toString()?.toUpperCase() ?? 'FALSE'}:mailto:${
      e.email
    }`;

  const lines = [
    'BEGIN:VCALENDAR',
    `VERSION:${version}`,
    `PRODID:${prodid}`,
    `METHOD:${method}`,
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${toTs(NovaDate.tzNow())}`,
    `DTSTART:${toTs(start)}`,
    `DTEND:${toTs(end)}`,
    `SUMMARY:${title}`,
    `LOCATION:${location}`,
    `DESCRIPTION:${description}`,
    organizer
      ? `ORGANIZER;CN="${organizer.name}";SENT-BY="mailto:noreply@nova-mg.opendock.com":mailto:${organizer.email}`
      : null,
    ...attendees.map(e => `ATTENDEE;CN=${entityToStr(e)}`),
    transparent ? 'TRANSP:TRANSPARENT' : 'TRANSP:OPAQUE',
    'END:VEVENT',
    'END:VCALENDAR'
  ].filter((line: string | null): line is string => Boolean(line));

  return `${lines.join('\r\n')}\r\n`;
}
